import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Home, Plus, Menu, X, LogOut, Bell } from "lucide-react";
import { Logo } from "@/components/marketing/Nav";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/lib/i18n";
import api from "@/lib/api";

export function Drawer({ title, sub, onClose, testid, children }) {
  const { rtl } = useLocale();
  return (
    <aside data-testid={testid} className={`fixed bottom-0 top-0 z-50 flex w-full flex-col border-[#E7E5DF] bg-white sm:w-[400px] ${rtl ? "left-0 border-r" : "right-0 border-l"}`}>
      <div className="flex h-16 shrink-0 items-center justify-between border-b border-[#E7E5DF] px-5">
        <div><div className="font-semibold text-[#181E4B]">{title}</div>{sub && <div className="text-xs text-[#5E6282]">{sub}</div>}</div>
        <button data-testid={`${testid}-close`} onClick={onClose} aria-label="Close"><X size={20} /></button>
      </div>
      <div className="overflow-y-auto p-5">{children}</div>
    </aside>
  );
}

export default function ChatShell({ caseId, subtitle, children }) {
  const { user, logout } = useAuth();
  const { pk, lang, setLang, rtl } = useLocale();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [menu, setMenu] = useState(false);
  useEffect(() => { api.get("/cases").then((r) => setCases(r.data)).catch(() => {}); }, [caseId]);

  return (
    <div dir={rtl ? "rtl" : "ltr"} lang={lang} className={`flex h-screen bg-[#FBFAF7] ${rtl ? "font-urdu" : ""}`} data-testid="chat-root">
      <aside data-testid="chat-sidebar-nav" className={`${menu ? "fixed inset-y-0 z-50 flex" : "hidden"} w-64 shrink-0 flex-col bg-[#181E4B] text-white lg:flex`}>
        <div className="flex h-16 items-center justify-between px-5"><Logo dark to="/app" /><button className="lg:hidden" onClick={() => setMenu(false)}><X size={18} /></button></div>
        <nav className="space-y-1 px-3 text-sm">
          <Link to="/app" className="flex items-center gap-3 rounded-md px-3 py-2.5 hover:bg-white/10" data-testid="sidebar-home"><Home size={16} />Home</Link>
          <Link to="/app/new" className="flex items-center gap-3 rounded-md px-3 py-2.5 hover:bg-white/10" data-testid="sidebar-new-app-btn"><Plus size={16} />New application</Link>
        </nav>
        <div className="mt-6 px-5 text-xs tracking-wider text-white/60">IN PROGRESS</div>
        <div className="mt-2 flex-1 space-y-1 overflow-y-auto px-3">
          {cases.map((c) => (
            <Link key={c.id} to={`/app/cases/${c.id}`} data-testid={`sidebar-case-${c.id}`}
              className={`block rounded-md px-3 py-2.5 text-sm ${c.id === caseId ? "bg-white text-[#181E4B]" : "hover:bg-white/10"}`}>
              <div className="font-medium">{c.corridor?.destination} {c.visa_category?.replace(/_/g, " ")}</div>
              <div className={`text-xs ${c.id === caseId ? "text-[#5E6282]" : "text-white/70"}`}>{c.applicant_name}</div>
            </Link>
          ))}
        </div>
        <div className="space-y-1 border-t border-white/15 p-3 text-sm">
          <div className="flex items-center gap-3 px-3 py-2">
            <span className="grid h-8 w-8 place-items-center rounded-sm bg-[#F1A501] font-semibold text-[#14183E]">{(user?.name || "U")[0]}</span>
            <span className="min-w-0 flex-1 truncate">{user?.name}</span>
            <button onClick={() => { logout(); navigate("/"); }} aria-label="Sign out" data-testid="app-logout-btn"><LogOut size={16} /></button>
          </div>
        </div>
      </aside>
      {menu && <button className="fixed inset-0 z-40 bg-black/30 lg:hidden" onClick={() => setMenu(false)} aria-label="Close menu" />}

      <div className="flex min-w-0 flex-1 flex-col">
        <header data-testid="chat-top-header" className="flex h-16 shrink-0 items-center justify-between border-b border-[#E7E5DF] bg-white px-4">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setMenu(true)} data-testid="mobile-menu-btn" aria-label="Menu"><Menu size={20} /></button>
            <img src="/brand-kit/favicon-64.png" alt="Holavisto" className="h-9 w-9 rounded-sm" data-testid="chat-header-logo" />
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-[#181E4B]">Holavisto AI Assistant<span className="h-2 w-2 rounded-full bg-[#15803D]" /></div>
              <div className="text-xs text-[#5E6282]">{subtitle}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {pk && (
              <div className="flex gap-1 text-sm" data-testid="language-toggle">
                {[["en", "English"], ["ur", "اردو"]].map(([k, l]) => (
                  <button key={k} onClick={() => setLang(k)} data-testid={`language-toggle-${k}`} className={`rounded-md px-3 py-1.5 ${lang === k ? "border border-[#181E4B] font-semibold text-[#181E4B]" : "text-[#5E6282]"}`}>{l}</button>
                ))}
              </div>
            )}
            <Link to="/app/notifications" aria-label="Notifications" data-testid="app-notifications-btn"><Bell size={18} className="text-[#5E6282]" /></Link>
          </div>
        </header>
        {children}
      </div>
    </div>
  );
}
